import { Scale } from 'lucide-react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';

// The ONE way every disruption surface says "the engine is off here".
//
// Pre-run rebooking policy gate (rebooking TDS §18 "opt-in by absence"): when the
// station's governing policy carries no rebooking block, a run could only end
// POLICY_DISABLED for every group. Rather than let the operator start it and watch
// nothing happen, each surface withholds its rebook action and shows THIS — the
// reason plus the rule to check. Read the gate with `rebookingPolicyGate(steps)`
// from resolution-steps.ts; this component only renders it.

interface RebookingPolicyNoticeProps {
  /** The governing (disabling) policy — renders the "View policy" link when known. */
  policyUrn?: string | undefined;
  className?: string;
  'data-testid'?: string;
}

export function RebookingPolicyNotice({
  policyUrn,
  className,
  'data-testid': testId = 'rebooking-policy-gate',
}: RebookingPolicyNoticeProps) {
  const { t } = useTranslation('disruptions');
  return (
    <p
      className={cn(
        'flex items-start gap-1.5 rounded-md border border-dashed border-border px-2 py-1.5 text-[11px] text-muted-foreground',
        className,
      )}
      data-testid={testId}
    >
      <Scale className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
      <span>
        {t('policyNotice.text')}{' '}
        {policyUrn ? (
          <Link
            to={`/policies/${encodeURIComponent(policyUrn)}`}
            className="font-semibold text-primary hover:underline"
            data-testid={`${testId}-link`}
          >
            {t('policyNotice.viewPolicy')}
          </Link>
        ) : null}
      </span>
    </p>
  );
}
